import { useState, useMemo } from "react";
import { Minus, Plus, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

// Reusable ticket picker + Stripe checkout launcher. `layout` = "card" | "inline".
export const TicketCheckout = ({ event }) => {
  const tiers = (event.tiers || []).filter((t) => t.active !== false);
  const [tierId, setTierId] = useState(tiers[0]?.id || "");
  const [qty, setQty] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promo, setPromo] = useState(null);
  const [applying, setApplying] = useState(false);

  const selected = useMemo(() => tiers.find((t) => t.id === tierId), [tierId, tiers]);
  const base = selected ? selected.price * qty : 0;
  let discount = 0;
  if (promo && selected) {
    discount = promo.discount_type === "percent" ? (base * promo.value) / 100 : Math.min(promo.value, base);
  }
  const total = (base - discount).toFixed(2);
  const isPast = new Date(event.date).getTime() < Date.now();

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    if (!tierId) return toast.error("Pick a ticket tier first.");
    setApplying(true);
    try {
      const { data } = await api.post("/promo/validate", { code: promoCode, tier_id: tierId });
      if (data.valid) {
        setPromo(data);
        toast.success(data.message);
      } else {
        setPromo(null);
        toast.error(data.message || "Invalid code");
      }
    } catch {
      toast.error("Could not check that code.");
    } finally {
      setApplying(false);
    }
  };

  const checkout = async () => {
    if (!tierId) return toast.error("Pick a ticket tier first.");
    if (!name.trim()) return toast.error("Enter the name on the order.");
    if (!email.trim()) return toast.error("Enter your email for the ticket.");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        tier_id: tierId,
        quantity: qty,
        buyer_name: name,
        buyer_email: email,
        promo_code: promo ? promoCode.trim().toUpperCase() : null,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };

  if (isPast) {
    return (
      <div className="rounded-2xl border border-neutral-800 bg-[#0a0a0a] p-6 text-center" data-testid="event-past-notice">
        <p className="font-display text-xl font-bold uppercase text-neutral-300">This ride has wrapped</p>
        <p className="mt-2 text-sm text-neutral-500">Check out the recap gallery below — new dates dropping soon.</p>
      </div>
    );
  }

  if (!tiers.length) {
    return (
      <div className="rounded-2xl border border-neutral-800 bg-[#0a0a0a] p-6 text-center text-neutral-400">
        Tickets not on sale yet. Join the list to get notified.
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-cyan-500/20 bg-[#0a0a0a] p-6 shadow-2xl" data-testid="ticket-checkout">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-2xl font-extrabold uppercase text-cyan-50">Get Tickets</h3>
        <span className="flex items-center gap-1 font-mono-label text-[10px] uppercase text-neutral-500">
          <ShieldCheck className="h-3.5 w-3.5 text-green-500" /> Secure Stripe
        </span>
      </div>

      <div className="mt-5 space-y-2">
        {tiers.map((t) => {
          const soldOut = t.capacity && t.sold >= t.capacity;
          const active = t.id === tierId;
          return (
            <button
              key={t.id}
              disabled={soldOut}
              onClick={() => {
                setTierId(t.id);
                setPromo(null);
              }}
              data-testid={`ticket-tier-${t.id}`}
              className={`flex w-full items-center justify-between rounded-xl border p-4 text-left transition-all ${
                active ? "border-cyan-500 bg-cyan-950/30" : "border-neutral-700 bg-black/30 hover:border-cyan-500/50"
              } ${soldOut ? "cursor-not-allowed opacity-40" : ""}`}
            >
              <div>
                <div className="font-display text-lg font-bold uppercase text-stone-100">{t.name}</div>
                <div className="text-xs text-neutral-400">{t.description}</div>
                {soldOut && <div className="mt-1 font-mono-label text-[10px] uppercase text-red-400">Sold Out</div>}
              </div>
              <div className="font-display text-2xl font-black text-cyan-400">${t.price}</div>
            </button>
          );
        })}
      </div>

      <div className="mt-5 flex items-center justify-between">
        <span className="font-display text-base font-bold uppercase text-neutral-300">Quantity</span>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setQty(Math.max(1, qty - 1))}
            data-testid="qty-decrease-btn"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-700 text-cyan-400 hover:border-cyan-500"
          >
            <Minus className="h-4 w-4" />
          </button>
          <span className="w-8 text-center font-display text-xl font-black text-cyan-50" data-testid="qty-value">{qty}</span>
          <button
            onClick={() => setQty(Math.min(50, qty + 1))}
            data-testid="qty-increase-btn"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-700 text-cyan-400 hover:border-cyan-500"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        <input
          placeholder="Full name on order"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="checkout-name-input"
          className="w-full rounded-xl border border-neutral-700 bg-black px-4 py-3 text-sm text-stone-100 outline-none focus:border-cyan-500"
        />
        <input
          type="email"
          placeholder="Email (ticket + QR sent here)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="checkout-email-input"
          className="w-full rounded-xl border border-neutral-700 bg-black px-4 py-3 text-sm text-stone-100 outline-none focus:border-cyan-500"
        />
      </div>

      <div className="mt-5">
        <label className="font-mono-label text-[10px] uppercase tracking-wider text-neutral-500">Promo code</label>
        <div className="mt-1 flex gap-2">
          <input
            placeholder="EARLYBIRD"
            value={promoCode}
            onChange={(e) => { setPromoCode(e.target.value); setPromo(null); }}
            data-testid="promo-code-input"
            className="w-full rounded-xl border border-neutral-700 bg-black px-4 py-3 text-sm uppercase tracking-wider text-stone-100 outline-none focus:border-cyan-500"
          />
          <button
            onClick={applyPromo}
            disabled={applying}
            data-testid="promo-apply-btn"
            className="whitespace-nowrap rounded-xl border border-cyan-500 px-4 font-display font-bold uppercase text-cyan-400 transition-colors hover:bg-cyan-500 hover:text-black disabled:opacity-50"
          >
            Apply
          </button>
        </div>
        {promo && (
          <p className="mt-2 font-mono-label text-[11px] uppercase text-green-400" data-testid="promo-applied-msg">
            ✓ {promo.message}
          </p>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-neutral-800 pt-4">
        <span className="font-display text-base font-bold uppercase text-neutral-400">Total</span>
        <div className="text-right">
          {discount > 0 && (
            <span className="mr-2 font-display text-lg text-neutral-500 line-through" data-testid="checkout-base">${base.toFixed(2)}</span>
          )}
          <span className="font-display text-3xl font-black text-cyan-400" data-testid="checkout-total">${total}</span>
        </div>
      </div>

      <button
        onClick={checkout}
        disabled={busy}
        data-testid="checkout-submit-btn"
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-cyan-500 py-4 font-display text-lg font-black uppercase tracking-wide text-black  transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Buy Tickets"}
      </button>
      <p className="mt-3 text-center font-mono-label text-[10px] uppercase tracking-wider text-stone-600">
        Test card 4242 4242 4242 4242 · any future date · any CVC
      </p>
    </div>
  );
};
